/**
 * B76C · screen 2e — the dual ARRIVAL banner.
 *
 * One quiet `--signal-soft` block that says the athlete side is on and the
 * coaching side did not move. Shown once, dismissible, never again.
 *
 * ── WHY IT IS ARMED RATHER THAN DERIVED ───────────────────────────────────
 * "Render for `role === 'athlete_coach'` and remember that you did" would also
 * fire, once, for every account that was ALREADY dual before this shipped —
 * announcing an arrival that happened months ago. So the banner is ARMED by
 * the transition itself (the last step of onboarding-lite calls
 * `armDualArrivalBanner`) and cleared on dismiss. An account that never
 * crossed is never armed, and the surface is inert for it forever. This also
 * keeps the open founder question on the Lida backfill (coach-first vs
 * grandfathered dual) from silently resolving itself in the banner's favour.
 *
 * ── WHERE IT RENDERS ──────────────────────────────────────────────────────
 * Mounted once at the app root, like `<DraftLandedChip>`, and self-gating: no
 * user, not armed, or not on Today ⇒ renders null. That is the house pattern
 * for an arrival confirmation that has to survive the navigation which brings
 * the athlete to it, and it means this lane changes no athlete-surface bytes.
 *
 * ⚠️ PLACEMENT NOTE for reconciliation: the CD handoff draws this banner IN
 * FLOW, directly above Today's first-run content. In-flow placement is an edit
 * to `routes/today.tsx`, which belongs to another lane; this mount renders it
 * at the top of the Today viewport instead. Copy, tone, tokens and the
 * once-only behaviour are exactly as ratified.
 *
 * 🏷️ Ratified 2e copy. House register: no dashes, no exclamations.
 */

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import {
  DUAL_ARRIVAL_PENDING_KEY,
  readUserFlag,
  writeUserFlag,
} from '../../lib/userFlagState';
import styles from './DualArrivalBanner.module.css';

/** Ratified 2e copy (CD handoff, screen 02e-arrival-dual). */
export const DUAL_ARRIVAL_COPY = {
  title: 'Athlete training is on.',
  body: 'Your coaching side has not changed. Your team is where you left it.',
  dismiss: 'Dismiss',
} as const;

/** Arm the banner for this account. Called at the end of onboarding-lite. */
export function armDualArrivalBanner(userId: string | null | undefined): void {
  writeUserFlag(DUAL_ARRIVAL_PENDING_KEY, userId, true);
}

/** Is the banner armed for this account? Exported for the route + tests. */
export function isDualArrivalArmed(userId: string | null | undefined): boolean {
  return readUserFlag(DUAL_ARRIVAL_PENDING_KEY, userId);
}

export function DualArrivalBanner() {
  const { user } = useAuthContext();
  const location = useLocation();
  const viewerId = user?.id ?? null;
  const onToday = location.pathname === '/today' || location.pathname.endsWith('/today');
  const [armed, setArmed] = useState(false);

  // Re-read on every identity or route change: the arm happens on another
  // route (the last lite step) and this component is mounted the whole time,
  // so a value read once at mount would always be stale.
  useEffect(() => {
    setArmed(isDualArrivalArmed(viewerId));
  }, [viewerId, location.pathname]);

  if (!viewerId || !armed || !onToday) return null;

  const dismiss = () => {
    writeUserFlag(DUAL_ARRIVAL_PENDING_KEY, viewerId, false);
    setArmed(false);
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.banner} role="status" aria-live="polite">
        <div className={styles.text}>
          <div className={styles.title}>{DUAL_ARRIVAL_COPY.title}</div>
          <div className={styles.body}>{DUAL_ARRIVAL_COPY.body}</div>
        </div>
        <button
          type="button"
          className={styles.dismiss}
          onClick={dismiss}
          aria-label={DUAL_ARRIVAL_COPY.dismiss}
        >
          ×
        </button>
      </div>
    </div>
  );
}

export default DualArrivalBanner;
